import React, { useEffect } from 'react'
import { ArrowTopRightOnSquareIcon, XMarkIcon } from '@heroicons/react/24/outline'
import { Link } from 'react-router-dom'
import { Button, Heading, Text } from '../../components/ui'
import type {
  MarketingNewsAlert,
  MarketingNewsAlertSentiment,
  MarketingNewsAlertTriageStatus,
} from '../../types/marketing'

interface AlertaDetalheModalProps {
  alert: MarketingNewsAlert
  onClose: () => void
}

const TRIAGE_LABELS: Record<MarketingNewsAlertTriageStatus, string> = {
  novo: 'Novo',
  analisado: 'Analisado',
  promovido: 'Clipping',
  descartado: 'Descartado',
}

const SENTIMENT_LABELS: Record<MarketingNewsAlertSentiment, string> = {
  positivo: 'Positivo',
  neutro: 'Neutro',
  negativo: 'Negativo',
}

function formatDateTimeBr(value: string | null | undefined): string {
  if (!value) return '—'
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return value.slice(0, 10)
  return d.toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

function displayValue(value: string | number | null | undefined): string {
  if (value == null) return '—'
  const text = String(value).trim()
  return text.length > 0 ? text : '—'
}

function triageBadgeClass(status: MarketingNewsAlertTriageStatus): string {
  switch (status) {
    case 'novo':
      return 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300'
    case 'analisado':
      return 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300'
    case 'promovido':
      return 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300'
    case 'descartado':
      return 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300'
    default:
      return 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300'
  }
}

const DetailField: React.FC<{ label: string; value: React.ReactNode; className?: string }> = ({
  label,
  value,
  className,
}) => (
  <div className={className}>
    <Text size="xs" color="muted" className="uppercase tracking-wider">
      {label}
    </Text>
    <div className="mt-1 text-sm text-gray-900 dark:text-white whitespace-pre-wrap break-words">
      {value ?? '—'}
    </div>
  </div>
)

const AlertaDetalheModal: React.FC<AlertaDetalheModalProps> = ({ alert, onClose }) => {
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [onClose])

  const sourceLabel = alert.search_site?.vehicle?.name || alert.search_site?.name || '—'
  const keywords = (alert.matched_keywords || []).filter((item) => item.trim().length > 0)

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div
        className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="alerta-detalhe-titulo"
      >
        <div className="flex items-start justify-between gap-4 p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="min-w-0">
            <Heading
              as="h2"
              size="2xl"
              id="alerta-detalhe-titulo"
              className="text-gray-900 dark:text-white"
            >
              Detalhes do alerta
            </Heading>
            <Text size="sm" color="muted" className="mt-1 line-clamp-2">
              {alert.title}
            </Text>
          </div>
          <Button
            onClick={onClose}
            variant="outline"
            size="sm"
            className="flex items-center gap-1 shrink-0"
          >
            <XMarkIcon className="h-4 w-4" />
            Fechar
          </Button>
        </div>

        <div className="p-6 overflow-y-auto flex-1">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <DetailField label="Título" value={displayValue(alert.title)} className="md:col-span-2" />

            <DetailField
              label="URL"
              className="md:col-span-2"
              value={
                alert.url ? (
                  <a
                    href={alert.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-start gap-1 text-primary-600 dark:text-primary-400 hover:underline break-all"
                  >
                    {alert.url}
                    <ArrowTopRightOnSquareIcon className="h-4 w-4 shrink-0 mt-0.5" />
                  </a>
                ) : (
                  '—'
                )
              }
            />

            <DetailField label="Publicado" value={formatDateTimeBr(alert.published_at)} />
            <DetailField label="Fonte" value={sourceLabel} />
            <DetailField
              label="Site de monitoramento"
              value={displayValue(alert.search_site?.name)}
            />
            <DetailField
              label="Configuração de busca"
              value={displayValue(alert.search_config?.name)}
            />

            <DetailField
              label="Status"
              value={
                <span
                  className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${triageBadgeClass(alert.triage_status)}`}
                >
                  {TRIAGE_LABELS[alert.triage_status]}
                </span>
              }
            />

            <div className="md:col-span-2 pt-2 border-t border-gray-200 dark:border-gray-700">
              <Text size="sm" className="font-medium text-gray-900 dark:text-white">
                Análise de IA
              </Text>
            </div>

            <DetailField
              label="Sugestão IA"
              value={
                alert.analysis?.is_clipping == null ? (
                  '—'
                ) : (
                  <span
                    className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${
                      alert.analysis.is_clipping
                        ? 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300'
                        : 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300'
                    }`}
                  >
                    {alert.analysis.is_clipping ? 'É clipping' : 'Não é clipping'}
                  </span>
                )
              }
            />
            <DetailField
              label="Score"
              value={
                alert.analysis?.relevance_score != null
                  ? String(alert.analysis.relevance_score)
                  : '—'
              }
            />
            <DetailField
              label="Sentimento"
              value={
                alert.analysis?.sentiment
                  ? SENTIMENT_LABELS[alert.analysis.sentiment]
                  : '—'
              }
            />
            <DetailField
              label="Categoria sugerida"
              value={displayValue(alert.analysis?.suggested_category?.name)}
            />
            <DetailField
              label="Analisado em"
              value={formatDateTimeBr(alert.analysis?.analyzed_at)}
            />

            <DetailField
              label="Palavras-chave"
              className="md:col-span-2"
              value={
                keywords.length > 0 ? (
                  <div className="flex flex-wrap gap-1.5">
                    {keywords.map((keyword) => (
                      <span
                        key={keyword}
                        className="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200"
                      >
                        {keyword}
                      </span>
                    ))}
                  </div>
                ) : (
                  '—'
                )
              }
            />

            <DetailField
              label="Trecho da coleta"
              value={displayValue(alert.snippet)}
              className="md:col-span-2"
            />
            <DetailField
              label="Resumo da IA"
              value={displayValue(alert.analysis?.summary)}
              className="md:col-span-2"
            />
            <DetailField
              label="Justificativa da IA"
              value={displayValue(alert.analysis?.reason)}
              className="md:col-span-2"
            />

            <DetailField
              label="Clipping"
              value={
                alert.clipping_id ? (
                  <Link
                    to="/marketing/clipping"
                    className="text-primary-600 dark:text-primary-400 hover:underline"
                  >
                    Ver clipping
                  </Link>
                ) : (
                  '—'
                )
              }
            />
            <DetailField label="Revisado em" value={formatDateTimeBr(alert.reviewed_at)} />
            <DetailField label="Criado em" value={formatDateTimeBr(alert.created_at)} />
            <DetailField label="Atualizado em" value={formatDateTimeBr(alert.updated_at)} />
          </div>
        </div>
      </div>
    </div>
  )
}

export default AlertaDetalheModal
